import { ProductCardSkeleton, ProductRowSkeleton } from "@/design-system";

/**
 * The product-listing loading state — shimmer cards matching the active view
 * mode, per BEHAVIOR.md ("450ms skeleton state … `aria-busy`, pager dims to 60%
 * and ignores input, then the new grid fades up").
 *
 * The card and row internals come straight from the design system
 * (`ProductCardSkeleton` / `ProductRowSkeleton`); what lives here is the
 * *container* geometry, which has to mirror the real grid in
 * `ListProductComponent` / `ListProductNoFilter` rather than the handoff's own
 * `repeat(auto-fill, minmax(190px, 1fr))`. A skeleton on different column counts
 * or a different card height reflows the page the moment the data lands, which
 * defeats the point of showing one.
 *
 * Keep these in sync if the real listing changes — note all three are about **column
 * count and shape, not height**:
 *   - the grid classes below            → the `view === "grid"` branch
 *   - `mediaClassName="aspect-square"`  → the DS ProductCard's `dnd-pc__media`
 *   - the list branch's gapped column   → the DS ProductRow, which is self-bordered
 *
 * Height is the caller's job now: `ListProductComponent` measures the rendered results
 * block and reserves that height around this skeleton, so a shorter placeholder can no
 * longer collapse the page mid-interaction.
 *
 * The grid is now the reference's single auto-fill track
 * (`repeat(auto-fill, minmax(190px, 1fr))`, `Dens N Dente Tailwind.dc.html:653`)
 * rather than a breakpoint ladder, so column count follows the container instead of
 * the viewport — which is what makes it correct inside the 268px-sidebar layout.
 *
 * @param {"grid"|"list"} view  Which layout to stand in for.
 * @param {number} count        How many placeholders — pass the page size.
 */
export default function ProductsSkeleton({
  view = "grid",
  count = 12,
  cardMinHeight,
}) {
  // Upstream caps this at 48 (`skelCards`), the largest page size offered.
  const placeholders = Array.from(
    { length: Math.max(1, Math.min(count, 48)) },
    (_, i) => i
  );

  if (view === "list") {
    // The rows are individually bordered cards in a gapped column now — the DS
    // `ProductRow` carries its own border/shadow/radius, so the skeleton's defaults
    // (bordered, 64px thumb, 90px total height) line up without any overrides.
    return (
      <div aria-busy="true" className="flex flex-col gap-[10px]">
        {placeholders.map((i) => (
          <ProductRowSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div aria-busy="true" className="pc-grid">
      {placeholders.map((i) => (
        <ProductCardSkeleton
          key={i}
          mediaClassName="aspect-square"
          // The measured height of a REAL card (see ListProductComponent's
          // reservation effect). Hand-syncing the skeleton's anatomy to the card
          // has drifted twice already — the card keeps growing — so the height is
          // taken from the live DOM instead and the skeleton can never fall short
          // again. Undefined on first paint → natural height.
          style={cardMinHeight ? { minHeight: cardMinHeight } : undefined}
        />
      ))}
    </div>
  );
}
